import * as React from "react";
import * as uuid from "uuid";

import {
  JoinDrawerUserExperience_Step,
  OneTimePasswordUserExperience_Step,
  useJoinDrawer,
} from "../../providers";

import { Drawer, DrawerProps } from "../../drawer";

import { StepJoin, StepJoinProps } from "./step-join";
import { StepLogin, StepLoginProps } from "./step-login";
import { StepSendCode } from "./step-send-code";
import { StepVerifyCode, StepVerifyCodeProps } from "./step-verify-code";

export interface JoinDrawerFragmentProps {
  useInitialState: StepVerifyCodeProps["useInitialState"] &
    StepJoinProps["useInitialState"] &
    StepLoginProps["useInitialState"];

  allowJoining?: StepLoginProps["allowJoining"];

  handleBack: JoinDrawer_OneTimePasswordSecondStep_FragmentProps["handleBack"];
  handleClose: JoinDrawer_OneTimePasswordSecondStep_FragmentProps["handleClose"];
}

export const JoinDrawerFragment: React.FunctionComponent<
  JoinDrawerFragmentProps
> = (props) => {
  const { presentingUserExperienceStep } = useJoinDrawer();

  return (
    <React.Fragment>
      {presentingUserExperienceStep === JoinDrawerUserExperience_Step.JOIN && (
        <StepJoin useInitialState={props.useInitialState} />
      )}
      {presentingUserExperienceStep === JoinDrawerUserExperience_Step.LOGIN && (
        <StepLogin
          useInitialState={props.useInitialState}
          allowJoining={props.allowJoining}
        />
      )}
      {presentingUserExperienceStep ===
        OneTimePasswordUserExperience_Step.FIRST_STEP_SEND_CODE && (
        <StepSendCode />
      )}

      <JoinDrawer_OneTimePasswordSecondStep_Fragment
        useInitialState={props.useInitialState}
        handleBack={props.handleBack}
        handleClose={props.handleClose}
      />
    </React.Fragment>
  );
};

const isMobileCurrently = () => {
  const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
  const isMobileCurrently = !isDesktop;
  return isMobileCurrently;
};

interface JoinDrawer_OneTimePasswordSecondStep_FragmentProps {
  useInitialState: StepVerifyCodeProps["useInitialState"];
  handleBack: DrawerProps["handleBack"];
  handleClose: DrawerProps["handleClose"];
}
const JoinDrawer_OneTimePasswordSecondStep_Fragment: React.FunctionComponent<
  JoinDrawer_OneTimePasswordSecondStep_FragmentProps
> = (props) => {
  const { presentingUserExperienceStep } = useJoinDrawer();

  const [presentMobileVersion, setPresentMobileVersion] =
    React.useState(isMobileCurrently);

  const drawerGuid = React.useMemo(() => {
    return uuid.v4();
  }, []);

  const present = React.useMemo(() => {
    return (
      presentingUserExperienceStep ===
      OneTimePasswordUserExperience_Step.SECOND_STEP_VERIFY_CODE
    );
  }, [presentingUserExperienceStep]);

  const handleResize = React.useCallback(() => {
    setPresentMobileVersion(isMobileCurrently());
  }, [setPresentMobileVersion]);
  React.useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  type GeneratedClassName =
    `join-drawer-ux--one-time-password-inner-drawer--${Lowercase<OneTimePasswordUserExperience_Step>}`;
  const classNameForTheDrawer = React.useMemo((): GeneratedClassName => {
    return `join-drawer-ux--one-time-password-inner-drawer--${presentingUserExperienceStep.toLowerCase()}` as GeneratedClassName;
  }, [presentingUserExperienceStep]);

  return (
    <React.Fragment>
      {presentMobileVersion && (
        <Drawer
          guid={drawerGuid}
          present={present}
          className={classNameForTheDrawer}
          showBackButton={present}
          handleBack={props.handleBack}
          handleClose={props.handleClose}
          dontShowOverlay
        >
          {present && (
            <StepVerifyCode useInitialState={props.useInitialState} />
          )}
        </Drawer>
      )}
      {!presentMobileVersion && present && (
        <StepVerifyCode useInitialState={props.useInitialState} />
      )}
    </React.Fragment>
  );
};

export const useJoinDrawerFragment = (
  presentingUserExperienceStep: `${
    | JoinDrawerUserExperience_Step
    | OneTimePasswordUserExperience_Step}`
) => {
  type GeneratedClassName = `join-drawer-ux-${Lowercase<
    JoinDrawerUserExperience_Step | OneTimePasswordUserExperience_Step
  >}`;
  const classNameForTheDrawer = React.useMemo((): GeneratedClassName => {
    return `join-drawer-ux-${presentingUserExperienceStep.toLowerCase()}` as GeneratedClassName;
  }, [presentingUserExperienceStep]);

  const value = React.useMemo(
    () => ({
      classNameForTheDrawer,
    }),
    [classNameForTheDrawer]
  );

  return value;
};
